import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminRoute } from './admin/admin-route';
import { useIdentify } from './analytics/use-identify';
import { ExtensionHandoffPage } from './auth/extension-handoff-page';
import { LoginPage } from './auth/login-page';
import { OnboardingPage } from './auth/onboarding-page';
import { ProtectedRoute } from './auth/protected-route';
import { VerifyMagicLinkPage } from './auth/verify-magic-link-page';
import { BillingPage } from './billing/billing-page';
import { BrowsePage } from './browser/browse-page';
import { SessionPage } from './browser/session-page';
import { UsagePage } from './credits/usage-page';
import { CaptureNotice } from './extension/capture-notice';
import { useExtensionCaptures } from './extension/use-extension-captures';
import { useExtensionReportIssue } from './extension/use-extension-report-issue';
import { FixListPrintPage } from './fix-list/fix-list-print-page';
import { AuditImportPage } from './home/audit-import-page';
import { HomePage } from './home/home-page';
import { NewRoomPage } from './home/new-room-page';
import { AppLoading } from './marketing/app-loading';
import { SupportPage } from './support/support-page';
import { WHATSAPP_AVAILABLE } from './whatsapp/whatsapp-available';
import { WhatsappPage } from './whatsapp/whatsapp-page';

/**
 * The admin panel is lazily loaded, so its bundle — a datagrid, five run
 * renderers, nuqs — never ships to the overwhelming majority of users who
 * cannot open it anyway.
 */
const AdminApp = lazy(() =>
  import('./admin/admin-app').then((m) => ({ default: m.AdminApp })),
);

/** The research report, lazily loaded too: it brings its own serif, which
 *  the rest of the app never needs. */
const ReportPage = lazy(() =>
  import('./research-report/report-page').then((m) => ({
    default: m.ReportPage,
  })),
);

function App() {
  // App-wide, not per screen: a capture from the Chrome extension arrives while
  // the user is looking at their own page, so any open Floto tab has to be able
  // to take it, whichever screen it happens to be showing.
  useExtensionCaptures();
  // Same reasoning, same mount point — a PM reports an issue from whatever
  // page they're on, not from a screen inside Floto.
  useExtensionReportIssue();
  // Once per session, here rather than inside useAuth — see the hook's note.
  useIdentify();

  return (
    <>
      <CaptureNotice />
      <Routes>
        <Route element={<LoginPage />} path="/login" />
        <Route element={<VerifyMagicLinkPage />} path="/login/verify-link" />
        <Route element={<OnboardingPage />} path="/login/onboarding" />
        <Route element={<ExtensionHandoffPage />} path="/auth/extension" />
        <Route
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
          path="/"
        />
        <Route
          element={
            <ProtectedRoute>
              <BrowsePage />
            </ProtectedRoute>
          }
          path="/browse"
        />
        <Route
          element={
            <ProtectedRoute>
              <NewRoomPage />
            </ProtectedRoute>
          }
          path="/new"
        />
        <Route
          element={
            <ProtectedRoute>
              <AuditImportPage />
            </ProtectedRoute>
          }
          path="/rooms/from-audit"
        />
        {/* No /api-keys route: API keys only serve the MCP server, which is
            not part of v1. The page is kept, unrouted, for when it is. */}
        <Route
          element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          }
          path="/billing"
        />
        <Route
          element={
            <ProtectedRoute>
              <UsagePage />
            </ProtectedRoute>
          }
          path="/billing/usage"
        />
        <Route
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
          path="/support"
        />
        <Route
          element={
            WHATSAPP_AVAILABLE ? (
              <ProtectedRoute>
                <WhatsappPage />
              </ProtectedRoute>
            ) : (
              <Navigate replace to="/" />
            )
          }
          path="/whatsapp"
        />
        {/* Projects are internal now — users only see rooms. Old links go
            home, where every room is. */}
        <Route
          element={<Navigate replace to="/" />}
          path="/projects/:projectId"
        />
        <Route
          element={
            <ProtectedRoute>
              <SessionPage />
            </ProtectedRoute>
          }
          path="/sessions/:sessionId"
        />
        <Route
          element={
            <ProtectedRoute>
              <FixListPrintPage />
            </ProtectedRoute>
          }
          path="/sessions/:sessionId/fixes/:captureId"
        />
        <Route
          element={
            <ProtectedRoute>
              <Suspense
                fallback={<div className="min-h-svh bg-warm" role="status" />}
              >
                <ReportPage />
              </Suspense>
            </ProtectedRoute>
          }
          path="/sessions/:sessionId/report/:roomId"
        />
        <Route
          element={
            <AdminRoute>
              <Suspense fallback={<AppLoading />}>
                <AdminApp />
              </Suspense>
            </AdminRoute>
          }
          path="/admin/*"
        />
        <Route element={<Navigate replace to="/" />} path="*" />
      </Routes>
    </>
  );
}

export default App;
